import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import deleteHhData from "../http/deleteHhData";
import deleteHhDataTemp from "../http/deleteHhDataTemp";
import getAsyncStatus, {
  type AsyncOperationStatus,
} from "../http/getAsyncStatus";
import getHhData from "../http/getHhData";
import getHhDataConfig from "../http/getHhDataConfig";
import getHhDataMetadata from "../http/getHhDataMetadata";
import getHhDataTemp from "../http/getHhDataTemp";
import getHhDataTempConfig from "../http/getHhDataTempConfig";
import postHhDataChartData, {
  type HhDataChartDataOptions,
} from "../http/postHhDataChartData";
import postHhDataCommitMappings, {
  type CommitMappingsRequest,
} from "../http/postHhDataCommitMappings";
import postHhDataDownload from "../http/postHhDataDownload";
import postHhDataDownloadZip, {
  type DownloadZipRequest,
} from "../http/postHhDataDownloadZip";
import postHhDataEdit from "../http/postHhDataEdit";
import postHhDataMatchToSites, {
  type MatchToSitesRequest,
} from "../http/postHhDataMatchToSites";
import postHhDataTempActualDemandFromSite, {
  type ActualDemandFromSiteRequest,
} from "../http/postHhDataTempActualDemandFromSite";
import postHhDataTempCommit from "../http/postHhDataTempCommit";
import postHhDataTempDetectMultiCurve from "../http/postHhDataTempDetectMultiCurve";
import postHhDataTempErrorAnalysis, {
  type ErrorAnalysisRequest,
  type ErrorAnalysisResponse,
} from "../http/postHhDataTempErrorAnalysis";
import postHhDataTempLogs from "../http/postHhDataTempLogs";
import postHhDataTempProcess from "../http/postHhDataTempProcess";
import postHhDataTempProcessAuto from "../http/postHhDataTempProcessAuto";
import postHhDataTempSplitIntoCurves, {
  type SplitIntoCurvesRequest,
} from "../http/postHhDataTempSplitIntoCurves";
import postHhDataTempUploadUrl from "../http/postHhDataTempUploadUrl";
import putHhDataTemp from "../http/putHhDataTemp";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

interface UploadPayload {
  file: File;
}

interface ProcessPayload {
  fileGuid: string;
  config?: any;
  source?: "auto" | "ai_fix" | "manual";
}

interface FetchLogsPayload {
  fileGuid: string;
  logPayload: any;
}

interface FetchLogsResult {
  logsUrl: string;
  siteId: string;
  requestId: string;
  objectKey: string;
  logCount: number;
  message: string;
  logsContent: string;
}

const useDemandData = () => {
  const queryClient = useQueryClient();
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  const fetchHhDataTempList = useQuery(
    ["hh-data-temp"],
    async () => {
      return getHhDataTemp(await getAuthTokenBlocking(getToken, relogin));
    },
    {
      // Allow query to be mounted multiple times on the same page without re-fetching
      staleTime: 5 * 60 * 1000,
    }
  );

  const fetchHhDataList = useQuery(
    ["hh-data"],
    async () => {
      return getHhData(await getAuthTokenBlocking(getToken, relogin));
    },
    {
      // Allow query to be mounted multiple times on the same page without re-fetching
      staleTime: 5 * 60 * 1000,
    }
  );

  const fetchHhDataTempConfig = useMutation(async (fileGuid: string) => {
    return getHhDataTempConfig(
      await getAuthTokenBlocking(getToken, relogin),
      fileGuid
    );
  });

  const uploadHhDataTemp = useMutation(
    async ({ file }: UploadPayload) => {
      const { name, type: contentType } = file;
      const uploadUrlResponse = await postHhDataTempUploadUrl(
        await getAuthTokenBlocking(getToken, relogin),
        {
          fileName: name,
          contentType,
        }
      );
      await putHhDataTemp(uploadUrlResponse.url, contentType, file);
      return {
        fileGuid: uploadUrlResponse.fileGuid,
        fileName: name,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hh-data-temp"]);
      },
    }
  );

  const actualDemandFromSite = useMutation(
    async (request: ActualDemandFromSiteRequest) => {
      return postHhDataTempActualDemandFromSite(
        await getAuthTokenBlocking(getToken, relogin),
        request
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hh-data-temp"]);
      },
    }
  );

  const processHhDataTemp = useMutation(
    async ({ fileGuid, config, source }: ProcessPayload) => {
      return postHhDataTempProcess(
        await getAuthTokenBlocking(getToken, relogin),
        fileGuid,
        { config, source }
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hh-data-temp"]);
      },
    }
  );

  const processHhDataTempAuto = useMutation(
    async (fileGuid: string) => {
      return postHhDataTempProcessAuto(
        await getAuthTokenBlocking(getToken, relogin),
        fileGuid
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hh-data-temp"]);
      },
    }
  );

  const detectMultiCurve = useMutation(async (fileGuid: string) => {
    return postHhDataTempDetectMultiCurve(
      await getAuthTokenBlocking(getToken, relogin),
      fileGuid
    );
  });

  const splitIntoCurves = useMutation(
    async ({
      fileGuid,
      request,
    }: {
      fileGuid: string;
      request: SplitIntoCurvesRequest;
    }) => {
      return postHhDataTempSplitIntoCurves(
        await getAuthTokenBlocking(getToken, relogin),
        fileGuid,
        request
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hh-data-temp"]);
      },
    }
  );

  // The error coach is now a tier-2 LRO (its Bedrock tool-use loop runs for tens of
  // seconds to minutes, well past the 30s API Gateway budget that previously surfaced
  // as 503s). This mutation hides the kickoff+poll: it POSTs the kickoff, and if the
  // response is an LRO handle (operationKey + pending/running) it polls /async_status
  // every 3s until the operation completes/fails, resolving with the final analysis
  // fields. A cache hit returns synchronously (status completed) and skips polling.
  const fetchHhDataTempErrorAnalysis = useMutation(
    async ({
      fileGuid,
      request,
    }: {
      fileGuid: string;
      request: ErrorAnalysisRequest;
    }): Promise<ErrorAnalysisResponse> => {
      const authToken = await getAuthTokenBlocking(getToken, relogin);
      const kickoff = await postHhDataTempErrorAnalysis(authToken, fileGuid, request);

      // Cache hit / synchronous result - no operation to poll.
      if (!kickoff.operationKey || kickoff.status === "completed") {
        return kickoff;
      }

      // Poll the LRO until it finishes. 15-min cap mirrors the Lambda ceiling.
      const POLL_INTERVAL_MS = 3000;
      const TIMEOUT_MS = 15 * 60 * 1000;
      const startedAt = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - startedAt > TIMEOUT_MS) {
          return { error: "AI analysis timed out after 15 minutes." };
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const freshToken = await getAuthTokenBlocking(getToken, relogin);
        const status = await getAsyncStatus(
          freshToken,
          fileGuid,
          kickoff.operationKey
        );
        if (status.status === "completed") {
          return (status.result as ErrorAnalysisResponse) ?? {};
        }
        if (status.status === "failed") {
          return {
            error:
              status.error || "AI analysis failed - see logs for details.",
          };
        }
      }
    }
  );

  const deleteHhDataTempFile = useMutation(
    async (fileGuid: string) => {
      return deleteHhDataTemp(
        await getAuthTokenBlocking(getToken, relogin),
        fileGuid
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hh-data-temp"]);
      },
    }
  );

  const commitHhDataTemp = useMutation(
    async (fileGuid: string) => {
      return postHhDataTempCommit(
        await getAuthTokenBlocking(getToken, relogin),
        fileGuid
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hh-data-temp"]);
        queryClient.invalidateQueries(["hh-data"]);
      },
    }
  );

  const editHhData = useMutation(
    async (fileGuid: string) => {
      return postHhDataEdit(
        await getAuthTokenBlocking(getToken, relogin),
        fileGuid
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hh-data-temp"]);
      },
    }
  );

  const fetchHhDataTempLogs = useMutation(
    async ({
      fileGuid,
      logPayload,
    }: FetchLogsPayload): Promise<FetchLogsResult> => {
      const response = await postHhDataTempLogs(
        await getAuthTokenBlocking(getToken, relogin),
        fileGuid,
        { log_payload: logPayload }
      );

      // Fetch the logs content from the presigned URL
      const logsResponse = await fetch(response.logs_url);
      const logsContent = await logsResponse.text();

      return {
        logsUrl: response.logs_url,
        siteId: response.site_id,
        requestId: response.request_id,
        objectKey: response.object_key,
        logCount: response.log_count,
        message: response.message,
        logsContent,
      };
    }
  );

  const deleteHhDataFile = useMutation(
    async (fileGuid: string) => {
      return deleteHhData(
        await getAuthTokenBlocking(getToken, relogin),
        fileGuid
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hh-data"]);
      },
    }
  );

  const fetchHhDataMetadata = useMutation(async (fileGuid: string) => {
    return getHhDataMetadata(
      await getAuthTokenBlocking(getToken, relogin),
      fileGuid
    );
  });

  const fetchHhDataConfig = useMutation(async (fileGuid: string) => {
    return getHhDataConfig(
      await getAuthTokenBlocking(getToken, relogin),
      fileGuid
    );
  });

  const fetchHhDataDownloadUrl = useMutation(async (fileGuid: string) => {
    return postHhDataDownload(
      await getAuthTokenBlocking(getToken, relogin),
      fileGuid
    );
  });

  const fetchHhDataChartData = useMutation(
    async (options: HhDataChartDataOptions) => {
      return postHhDataChartData(
        await getAuthTokenBlocking(getToken, relogin),
        "hh_demand",
        options
      );
    }
  );

  const matchToSites = useMutation(async (request: MatchToSitesRequest) => {
    return postHhDataMatchToSites(
      await getAuthTokenBlocking(getToken, relogin),
      request
    );
  });

  const downloadHhDataZip = useMutation(async (request: DownloadZipRequest) => {
    return postHhDataDownloadZip(
      await getAuthTokenBlocking(getToken, relogin),
      request
    );
  });

  const commitMappings = useMutation(
    async (request: CommitMappingsRequest) => {
      return postHhDataCommitMappings(
        await getAuthTokenBlocking(getToken, relogin),
        request
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hh-data"]);
        queryClient.invalidateQueries(["hh-data-temp"]);
      },
    }
  );

  // Batch status check for the bulk Auto-process flow. Takes a list of (fileGuid,
  // operationKey) pairs and resolves with one AsyncOperationStatus per pair. The
  // bulk flow needs per-LRO outcome - file metadata alone can't distinguish
  // "still running" from "failed", because the LRO only writes processedTime on
  // success.
  const pollAutoOperationStatuses = useMutation(
    async (
      pairs: Array<{ fileGuid: string; operationKey: string }>
    ): Promise<
      Array<{ fileGuid: string; status: AsyncOperationStatus | null }>
    > => {
      const authToken = await getAuthTokenBlocking(getToken, relogin);
      return Promise.all(
        pairs.map(async ({ fileGuid, operationKey }) => {
          try {
            const status = await getAsyncStatus(authToken, fileGuid, operationKey);
            return { fileGuid, status };
          } catch {
            return { fileGuid, status: null };
          }
        })
      );
    }
  );

  return {
    fetchHhDataTempList,
    fetchHhDataList,
    fetchHhDataTempConfig,
    uploadHhDataTemp,
    actualDemandFromSite,
    processHhDataTemp,
    processHhDataTempAuto,
    fetchHhDataTempErrorAnalysis,
    detectMultiCurve,
    splitIntoCurves,
    deleteHhDataTempFile,
    commitHhDataTemp,
    editHhData,
    fetchHhDataTempLogs,
    deleteHhDataFile,
    fetchHhDataMetadata,
    fetchHhDataConfig,
    fetchHhDataDownloadUrl,
    fetchHhDataChartData,
    matchToSites,
    commitMappings,
    downloadHhDataZip,
    pollAutoOperationStatuses,
  };
};

export default useDemandData;
